import { DateOnly, format } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { ActionFooter, Caption, SelectionList, SelectionRow, Shortcut, SubsectionHeader } from '@if/mui'
import { Card, CardContent, Divider, ListItemText, Stack } from '@mui/material'
import { InteracWire, InteracWireFiltersType } from '@src/types/interac'
import { useTranslation } from 'react-i18next'
import InteracMatchResultChip from './interac-match-result'
import { InteracQueueCursor } from './interac-queue-cursor-hook'
import InteracQueueFilters, { InteracQueueFilterValues } from './InteracQueueFilters'

interface InteracQueueCardProps {
  queue: InteracWire[]
  cursor: InteracQueueCursor
  onSelect: (index: number) => void
  filters: InteracWireFiltersType
  isFiltered: boolean
  onFiltersChange: (values: InteracQueueFilterValues) => void
}

const InteracQueueCard = ({ queue, cursor, onSelect, filters, isFiltered, onFiltersChange }: InteracQueueCardProps) => {
  const { t } = useTranslation()

  return (
    <Card>
      <SubsectionHeader
        title={t('interacWire.queue.title')}
        subtitle={
          queue.length > 0
            ? t('interacWire.queue.position', { position: cursor.index + 1, total: queue.length })
            : undefined
        }
      />
      <CardContent sx={{ pt: 0, pb: 2 }}>
        <InteracQueueFilters filters={filters} onChange={onFiltersChange} />
      </CardContent>

      <Divider />

      {queue.length === 0 ? (
        <CardContent>
          <Caption>{isFiltered ? t('interacWire.queue.emptyFiltered') : t('interacWire.queue.empty')}</Caption>
        </CardContent>
      ) : (
        <SelectionList
          label={t('interacWire.queue.listLabel')}
          tabStop={false}
          activeDescendant={cursor.activeDescendant}
        >
          {queue.map((wire, index) => (
            <SelectionRow
              key={wire.id}
              id={cursor.itemId(index)}
              tabIndex={-1}
              selected={cursor.isActive(index)}
              onClick={() => onSelect(index)}
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span>{wire.sentBy}</span>
                    <span>{formatCurrency(wire.amount)}</span>
                  </Stack>
                }
                secondary={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}
                  >
                    <span>{`${format(new DateOnly(wire.receivedOn), 'P')} · ${wire.bankName}`}</span>
                    <InteracMatchResultChip systemMatchingResult={wire.systemMatchingResult} />
                  </Stack>
                }
                slotProps={{ primary: { component: 'div' }, secondary: { component: 'div' } }}
              />
            </SelectionRow>
          ))}
        </SelectionList>
      )}

      <ActionFooter leading={<Caption>{t('interacWire.queue.moveBetweenTransfers')}</Caption>}>
        <Shortcut displayOnly forceDisplay shortcut={{ key: 'ArrowUp' }} />
        <Shortcut displayOnly forceDisplay shortcut={{ key: 'ArrowDown' }} />
      </ActionFooter>
    </Card>
  )
}

export default InteracQueueCard
